import type { JSX } from 'react';
import type {
  CompanyDetailResource,
  CompanyOriginValue,
  CreateCompanyBody,
  DirectoryCompanyResource,
  UpdateCompanyBody,
} from '@majuscule/contracts';
import { companyOriginSchema } from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiHttpError, internalFetch } from '../../lib/api';
import { crmSend, errorMessage, ORIGIN_LABEL, ORIGINS } from '../../lib/crm';
import { MutationError, useCrmRefresh } from './shared';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { CheckRow, Field, FieldHint, Fields2, Fieldset } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';

interface IdentityValues {
  name: string;
  legalName: string;
  siren: string;
  nafCode: string;
  headcount: string;
  address: string;
  sector: string;
  origin: CompanyOriginValue;
}

const nullable = (value: string): string | null => (value.trim() === '' ? null : value.trim());

function identityOf(company: CompanyDetailResource | null): IdentityValues {
  return {
    name: company?.name ?? '',
    legalName: company?.legalName ?? '',
    siren: company?.siren ?? '',
    nafCode: company?.nafCode ?? '',
    headcount: company?.headcount ?? '',
    address: company?.address ?? '',
    sector: company?.sector ?? '',
    origin: company?.origin ?? 'network',
  };
}

/** SIREN typed by the operator: digits only (a SIRET is accepted by the API too). */
function sirenDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function IdentityFields({
  values,
  onChange,
  nameError,
}: {
  values: IdentityValues;
  onChange: (patch: Partial<IdentityValues>) => void;
  nameError: string | undefined;
}): JSX.Element {
  return (
    <>
      <Fieldset title="Identité">
        <Fields2>
          <Field label="Nom d’usage" required error={nameError}>
            <Input
              value={values.name}
              maxLength={200}
              onChange={(event) => {
                onChange({ name: event.target.value });
              }}
            />
          </Field>
          <Field label="Raison sociale">
            <Input
              value={values.legalName}
              maxLength={200}
              onChange={(event) => {
                onChange({ legalName: event.target.value });
              }}
            />
          </Field>
        </Fields2>
        <Fields2>
          <Field label="Code NAF">
            <Input
              value={values.nafCode}
              maxLength={20}
              onChange={(event) => {
                onChange({ nafCode: event.target.value });
              }}
            />
          </Field>
          <Field label="Effectif">
            <Input
              value={values.headcount}
              maxLength={100}
              onChange={(event) => {
                onChange({ headcount: event.target.value });
              }}
            />
          </Field>
        </Fields2>
        <Field label="Adresse">
          <Input
            value={values.address}
            maxLength={300}
            onChange={(event) => {
              onChange({ address: event.target.value });
            }}
          />
        </Field>
      </Fieldset>
      <Fieldset title="Suivi">
        <Fields2>
          <Field label="Origine" required>
            <NativeSelect
              value={values.origin}
              onChange={(event) => {
                const parsed = companyOriginSchema.safeParse(event.target.value);
                if (parsed.success) {
                  onChange({ origin: parsed.data });
                }
              }}
            >
              {ORIGINS.map((origin) => (
                <option key={origin} value={origin}>
                  {ORIGIN_LABEL[origin]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Secteur">
            <Input
              value={values.sector}
              maxLength={100}
              onChange={(event) => {
                onChange({ sector: event.target.value });
              }}
            />
          </Field>
        </Fields2>
      </Fieldset>
    </>
  );
}

type Lookup =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'found' }
  | { state: 'not_found' }
  | { state: 'error' };

/** « Nouvelle entreprise » (CompanyDialog.dc.html): SIREN lookup, identity, primary contact. */
export function CreateCompanyDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {open ? <CreateCompanyForm onClose={onClose} /> : null}
    </Dialog>
  );
}

function CreateCompanyForm({ onClose }: { onClose: () => void }): JSX.Element {
  const refresh = useCrmRefresh();
  const navigate = useNavigate();
  const [values, setValues] = useState<IdentityValues>(identityOf(null));
  const [contact, setContact] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [qualifying, setQualifying] = useState(true);
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  const [submitted, setSubmitted] = useState(false);

  const patch = (next: Partial<IdentityValues>): void => {
    setValues((current) => ({ ...current, ...next }));
  };

  const search = async (): Promise<void> => {
    const siren = sirenDigits(values.siren).slice(0, 9);
    if (siren.length !== 9) {
      setLookup({ state: 'not_found' });
      return;
    }
    setLookup({ state: 'loading' });
    try {
      const found = await internalFetch<DirectoryCompanyResource>(
        `/internal/crm/directory/${siren}`,
      );
      setValues((current) => ({
        ...current,
        siren: found.siren,
        name: current.name.trim() === '' ? found.name : current.name,
        legalName: found.legalName,
        nafCode: found.nafCode ?? current.nafCode,
        headcount: found.headcount ?? current.headcount,
        address: found.address ?? current.address,
      }));
      setLookup({ state: 'found' });
    } catch (error) {
      setLookup({
        state: error instanceof ApiHttpError && error.status === 404 ? 'not_found' : 'error',
      });
    }
  };

  const hasContact = Object.values(contact).some((value) => value.trim() !== '');
  const errors = {
    name: values.name.trim() === '' ? 'Indiquer le nom d’usage.' : undefined,
    firstName:
      hasContact && contact.firstName.trim() === '' ? 'Prénom requis pour le contact.' : undefined,
    lastName:
      hasContact && contact.lastName.trim() === '' ? 'Nom requis pour le contact.' : undefined,
  };
  const valid = Object.values(errors).every((error) => error === undefined);
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);

  const create = useMutation({
    mutationFn: () => {
      const body: CreateCompanyBody = {
        name: values.name.trim(),
        legalName: nullable(values.legalName),
        siren: nullable(values.siren),
        nafCode: nullable(values.nafCode),
        headcount: nullable(values.headcount),
        address: nullable(values.address),
        sector: nullable(values.sector),
        origin: values.origin,
        primaryContact: hasContact
          ? {
              firstName: contact.firstName.trim(),
              lastName: contact.lastName.trim(),
              email: nullable(contact.email),
              phone: nullable(contact.phone),
            }
          : null,
        createQualifyingDeal: qualifying,
      };
      return crmSend<CompanyDetailResource>('POST', '/companies', body);
    },
    onSuccess: (company) => {
      refresh();
      onClose();
      void navigate({ to: '/companies/$companyId', params: { companyId: company.id } });
    },
  });

  return (
    <DialogContent width={640}>
      <DialogHead
        title="Nouvelle entreprise"
        description="Créer un prospect à partir de son SIREN."
      />
      <form
        id="company-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            create.mutate();
          }
        }}
      >
        <DialogBody>
          <Field
            label="SIREN"
            hint="Raison sociale, adresse, code NAF et effectif sont repris de l’annuaire des entreprises. Sans SIREN, tout se saisit à la main."
          >
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
              <Input
                numeric
                inputMode="numeric"
                maxLength={20}
                value={values.siren}
                onChange={(event) => {
                  patch({ siren: event.target.value });
                  setLookup({ state: 'idle' });
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={lookup.state === 'loading' || values.siren.trim() === ''}
                onClick={() => {
                  void search();
                }}
              >
                Rechercher
              </Button>
            </div>
          </Field>

          {lookup.state === 'found' ? (
            <Callout variant="info" title="Entreprise trouvée.">
              Les champs ci-dessous sont préremplis et restent modifiables.
            </Callout>
          ) : null}
          {lookup.state === 'not_found' ? (
            <Callout variant="warning" title="Entreprise introuvable.">
              Aucun établissement ne correspond à ce SIREN dans l’annuaire : vérifier les 9
              chiffres, ou saisir les informations à la main.
            </Callout>
          ) : null}
          {lookup.state === 'error' ? (
            <Callout variant="destructive">
              L’annuaire des entreprises ne répond pas. Réessayer, ou saisir les informations à la
              main.
            </Callout>
          ) : null}

          <IdentityFields values={values} onChange={patch} nameError={shown(errors.name)} />

          <Fieldset title="Contact principal">
            <Fields2>
              <Field label="Prénom" error={shown(errors.firstName)}>
                <Input
                  value={contact.firstName}
                  maxLength={100}
                  onChange={(event) => {
                    setContact({ ...contact, firstName: event.target.value });
                  }}
                />
              </Field>
              <Field label="Nom" error={shown(errors.lastName)}>
                <Input
                  value={contact.lastName}
                  maxLength={100}
                  onChange={(event) => {
                    setContact({ ...contact, lastName: event.target.value });
                  }}
                />
              </Field>
            </Fields2>
            <Fields2>
              <Field label="Email">
                <Input
                  type="email"
                  value={contact.email}
                  maxLength={200}
                  onChange={(event) => {
                    setContact({ ...contact, email: event.target.value });
                  }}
                />
              </Field>
              <Field label="Téléphone">
                <Input
                  type="tel"
                  value={contact.phone}
                  maxLength={40}
                  onChange={(event) => {
                    setContact({ ...contact, phone: event.target.value });
                  }}
                />
              </Field>
            </Fields2>
            <FieldHint>Optionnel. D’autres contacts s’ajoutent depuis la fiche.</FieldHint>
          </Fieldset>

          <CheckRow
            label="Créer une affaire À qualifier avec un rappel demain à 9 h 00"
            checked={qualifying}
            onChange={(event) => {
              setQualifying(event.target.checked);
            }}
          />
          <MutationError
            message={create.isError ? errorMessage(create.error, 'Échec de la création.') : null}
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="company-form" size="lg" disabled={create.isPending}>
          Créer l’entreprise
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/** « Modifier les informations » of a company (PATCH, identity, origin, primary contact). */
export function EditCompanyDialog({
  company,
  onClose,
}: {
  company: CompanyDetailResource | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={company !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {company !== null ? <EditCompanyForm company={company} onClose={onClose} /> : null}
    </Dialog>
  );
}

function EditCompanyForm({
  company,
  onClose,
}: {
  company: CompanyDetailResource;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [values, setValues] = useState<IdentityValues>(identityOf(company));
  const [primaryContactId, setPrimaryContactId] = useState(company.primaryContactId ?? '');
  const [submitted, setSubmitted] = useState(false);
  const nameError = values.name.trim() === '' ? 'Indiquer le nom d’usage.' : undefined;

  const save = useMutation({
    mutationFn: () => {
      const body: UpdateCompanyBody = {
        name: values.name.trim(),
        legalName: nullable(values.legalName),
        siren: nullable(values.siren),
        nafCode: nullable(values.nafCode),
        headcount: nullable(values.headcount),
        address: nullable(values.address),
        sector: nullable(values.sector),
        origin: values.origin,
        primaryContactId: primaryContactId === '' ? null : primaryContactId,
      };
      return crmSend<CompanyDetailResource>('PATCH', `/companies/${company.id}`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  return (
    <DialogContent width={640} aria-describedby={undefined}>
      <DialogHead title="Modifier les informations" description={company.name} />
      <form
        id="company-edit-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (nameError === undefined) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          <Field label="SIREN" hint="9 chiffres ; un SIRET est aussi accepté.">
            <Input
              numeric
              inputMode="numeric"
              maxLength={20}
              value={values.siren}
              onChange={(event) => {
                setValues({ ...values, siren: event.target.value });
              }}
            />
          </Field>
          <IdentityFields
            values={values}
            onChange={(next) => {
              setValues((current) => ({ ...current, ...next }));
            }}
            nameError={submitted ? nameError : undefined}
          />
          <Field label="Contact principal">
            <NativeSelect
              value={primaryContactId}
              onChange={(event) => {
                setPrimaryContactId(event.target.value);
              }}
            >
              <option value="">Aucun</option>
              {company.contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.firstName} {contact.lastName}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <MutationError
            message={save.isError ? errorMessage(save.error, 'Échec de l’enregistrement.') : null}
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="company-edit-form" size="lg" disabled={save.isPending}>
          Enregistrer
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
